/**
 * Best Friend Instagram Configuration
 *
 * Lista de Close Friends de Instagram — acceso al contenido exclusivo (stories,
 * publicaciones privadas y contenido detrás de cámaras) que la creadora comparte
 * solo con su "mejor amiga" digital.
 *
 * Gateway de pago según país (membership tipo suscripción):
 * - Argentina (AR): MercadoPago con precio en ARS (env `VITE_BESTFRIEND_INSTAGRAM_PRICE_ARS`)
 * - Otros países: Stripe con precio en USD (env `VITE_BESTFRIEND_INSTAGRAM_PRICE_USD`)
 *
 * Sin email input — el checkout pide los datos directamente al gateway.
 *
 * Para habilitar en un país:
 * 1. Agregar el código ISO del país a `enabledCountries` (env `VITE_BESTFRIEND_INSTAGRAM_COUNTRIES`)
 * 2. Configurar precios en ARS y/o USD
 * 3. Configurar la URL de éxito (puede ser un form, página de bienvenida, etc.)
 */

export interface BestFriendInstagramConfig {
  /** Precio mensual en ARS para Argentina (MercadoPago) */
  priceARS: number;
  /** Precio mensual en USD para otros países (Stripe) */
  priceUSD: number;
  /** Países donde se muestra (códigos ISO, e.g. ["AR", "MX", "US"]) */
  enabledCountries: string[];
  /** URL de éxito después del pago (form, bienvenida, etc.) */
  successUrl: string;
  /** URL de imagen de fondo para la página (opcional) */
  backgroundImageUrl?: string;
}

/**
 * Configuración global de Best Friend Instagram.
 * Todos los valores son env-driven con defaults razonables.
 */
export const BESTFRIEND_INSTAGRAM_CONFIG: BestFriendInstagramConfig = {
  // Precio mensual en ARS (Argentina · MercadoPago membership/subscription)
  priceARS: parseFloat(import.meta.env.VITE_BESTFRIEND_INSTAGRAM_PRICE_ARS || "6999"),

  // Precio mensual en USD (otros países · Stripe membership/subscription)
  priceUSD: parseFloat(import.meta.env.VITE_BESTFRIEND_INSTAGRAM_PRICE_USD || "9.99"),

  // Países habilitados (agregar/quitar según necesidad)
  enabledCountries: (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_COUNTRIES || "AR,MX,ES")
    .split(",")
    .map((c: string) => c.trim())
    .filter(Boolean),

  // URL de éxito (form, página de bienvenida, etc.)
  successUrl: import.meta.env.VITE_BESTFRIEND_INSTAGRAM_SUCCESS_URL || "",

  // Imagen de fondo personalizada
  backgroundImageUrl: import.meta.env.VITE_BESTFRIEND_INSTAGRAM_BG_IMAGE || undefined,
};

export const isBestFriendInstagramEnabledForCountry = (countryCode: string): boolean =>
  BESTFRIEND_INSTAGRAM_CONFIG.enabledCountries.includes(countryCode);

export const getBestFriendInstagramConfig = (countryCode: string): BestFriendInstagramConfig | null => {
  if (!isBestFriendInstagramEnabledForCountry(countryCode)) return null;
  return BESTFRIEND_INSTAGRAM_CONFIG;
};

export const getBestFriendInstagramConfigForCountry = (
  countryCode: string,
): BestFriendInstagramConfig | null => getBestFriendInstagramConfig(countryCode);
